// src/components/shared/TippsTricksModal.jsx
// Reine Info-Box (nur "Schließen", kein Bestätigen/Abbrechen) - deshalb ein eigenes,
// leichtgewichtiges Modal statt useDialog() aus Dialog.jsx, das auf Promise-basierte
// confirm()/alert()-Aufrufe mit kurzem Text zugeschnitten ist. Der Inhalt ist bewusst als
// echtes JSX geschrieben (keine dangerouslySetInnerHTML) - der Text ist statisch und im Code
// gepflegt, sodass kein HTML aus einer externen Quelle eingebunden werden muss.
import React, { useEffect } from 'react'

function TippsTricksModal ({ onClose }) {
  // Esc schließt das Modal, wie bei Dialog.jsx / MultiImageCapture gewohnt.
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Body-Scroll sperren, solange das Modal offen ist. Ohne das kann die Seite dahinter
  // gescrollt werden, während vorne ein position:fixed-Overlay liegt - auf mobilen Browsern
  // (v.a. iOS Safari) führt das dazu, dass das fixed-Element nicht mehr exakt zum sichtbaren
  // Viewport zentriert wird, sondern seitlich versetzt erscheint (genau das gemeldete
  // "volle Breite, aber nach rechts verschoben"-Symptom). Wichtig: die vorherige Einstellung
  // beim Aufräumen wiederherstellen statt hart auf '' zu setzen, falls z.B. mal zwei Dialoge
  // gleichzeitig offen wären (Dialog.jsx + dieses Modal).
  useEffect(() => {
    const vorherigerOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = vorherigerOverflow
    }
  }, [])

  return (
    <div className='dialog-overlay' onClick={onClose}>
      <div
        className='dialog-box tipps-tricks-box'
        onClick={e => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label='Tipps & Tricks'
      >
        <h3 className='dialog-titel'>Tipps &amp; Tricks</h3>

        <div className='tipps-tricks-inhalt'>
          <h4>PC</h4>
          <ul>
            <li>
              <strong>Für besseren Überblick: Im Vollbild arbeiten:</strong>{' '}
              Firefox/Chrome/Edge: Taste <strong>F11</strong>. Safari (Mac):{' '}
              <strong>Strg + Cmd + F</strong>. Mit derselben Taste wieder
              verlassen.
            </li>
            <li>
              <strong>Fertige PDF-Dokumente oder Bilder auswählen</strong> statt
              zu fotografieren – liefert die beste Erkennungsqualität.
            </li>
            <li>
              <strong>Papierdokumente einscannen:</strong> mit{' '}
              <strong>300 dpi</strong> und der Einstellung{' '}
              <strong>Graustufen</strong> (nicht Schwarz-Weiß) scannen. Farbe
              wird nicht benötigt und macht die Dateien nur unnötig groß.
            </li>
            <li>
              <strong>Typischer Arbeitsablauf:</strong> „Rechnung auswählen“
              oder „Mehrere Rechnungen auswählen“ → auf{' '}
              <strong>„Extrahieren“</strong> klicken → Felder im Eingabeformular
              prüfen und bei Bedarf korrigieren (rot/gelb markierte Felder
              zeigen unsichere oder fehlende Werte) - wenn eine Zeile in der
              Übersicht komplett gelb markiert ist, im Eingabeformular die zu
              verwendende IBAN auswählen → mit <strong>„Übernehmen“</strong> in
              die Liste aufnehmen → weitere Rechnungen erfassen → am Ende auf{' '}
              <strong>„Export“</strong> klicken, um alle erfassten Rechnungen
              als Excel-Datei herunterzuladen.
            </li>
            <li>
              Einträge in der Liste können jederzeit über den Bearbeiten-Button
              korrigiert oder über den Löschen-Button entfernt werden – auch
              nachträglich, vor dem Export. Über „🗑 Alle löschen“ lässt sich die
              gesamte Liste auf einmal leeren.
            </li>
          </ul>

          <h4>Mobil</h4>
          <ul>
            <li>
              Auf <strong>gleichmäßige, helle Beleuchtung</strong> achten –
              Schatten und Blendungen erschweren die Erkennung.
            </li>
            <li>
              Das <strong>ganze Dokument</strong> muss im Bild sein, im{' '}
              <strong>Hochformat</strong> fotografieren und auf{' '}
              <strong>Schärfe</strong> achten (nicht verwackelt). Bei
              ungünstigen Aufnahmen kann eine Warnung erscheinen.
            </li>
            <li>
              Technisch bedingt lässt sich mit einem Foto{' '}
              <strong>nicht dieselbe Erkennungsqualität</strong> erreichen wie
              mit einem Scanner oder einem fertigen PDF – bei wichtigen oder
              schlecht lesbaren Belegen daher wenn möglich scannen statt
              fotografieren.
            </li>
            <li>
              Auf <strong>Tablets mit großem Bildschirm</strong>, die als
              Desktop erkannt werden: über den Schalter{' '}
              <strong>„📱 Mobile Ansicht“</strong> oben rechts im Header lässt
              sich die Kamera-Aufnahme manuell einblenden.
            </li>
            <li>
              <strong>Typischer Arbeitsablauf:</strong> Foto aufnehmen (oder
              mehrseitige Rechnung Seite für Seite fotografieren) → auf{' '}
              <strong>„Extrahieren“</strong>
              klicken → Felder prüfen/korrigieren →{' '}
              <strong>„Übernehmen“</strong> → weitere Rechnungen erfassen →
              Export bei Mobilgeräten am einfachsten mit{' '}
              <strong>„📧 Export als Mail“</strong>.
            </li>
          </ul>
        </div>

        <div className='dialog-aktionen'>
          <button
            type='button'
            className='btn btn-primary'
            onClick={onClose}
            autoFocus
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  )
}

export default TippsTricksModal
